/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
	BASE_PATH,
	BaseAPI,
	ClientExceptionEntity,
	ClientExceptionEntityTypeEnum,
	FetchParams,
	Middleware,
	RequestContext,
	ResponseContext
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { BACKEND_URL } from './AuthSettings';
import { ClientExceptionError } from '../../shared/errors/Errors';
import { getAccessToken } from '../../modules/auth/authToken';

const AUTHORIZATION_HEADER = 'Authorization';
const APP_VARIANT_ID_HEADER = 'appVariantIdentifier';
const APP_VARIANT_ID = 'de.fhg.iese.dd.platform.adminui';

class DigitaleDoerferMiddleware implements Middleware {
	private static createAuthenticationHeader(token: string): string {
		return 'Bearer ' + token;
	}

	async pre(context: RequestContext): Promise<FetchParams | void> {
		const { init, url } = context;
		const accessToken = getAccessToken();
		if (!accessToken) return;

		const authenticationHeader = DigitaleDoerferMiddleware.createAuthenticationHeader(accessToken);
		const headers = new Headers(init.headers);
		headers.set(AUTHORIZATION_HEADER, authenticationHeader);
		headers.set(APP_VARIANT_ID_HEADER, APP_VARIANT_ID);
		init.headers = headers;
		const newUrl = BACKEND_URL + url.substr(BASE_PATH.length + 1);
		return {
			init,
			url: newUrl
		};
	}

	async post(context: ResponseContext): Promise<Response | void> {
		if (context.response.status >= 400) {
			// Server returned with HTTP error code, so response is ClientException.
			// If for certain cases, this is not true, add handling here.
			let errorResponse: ClientExceptionEntity;
			try {
				errorResponse = await context.response.json();
			} catch {
				// Response body is empty or not valid JSON (e.g. 403 from gateway/proxy
				// with no body). Fall back to a generic error so the actual HTTP status
				// is visible instead of masking it with a SyntaxError.
				//
				// Note that here it is assumed that the underlying, autogenerated code of runtime.ts
				// does not "swallow" the exception.
				// See https://erbenschell.iese.fraunhofer.de/DigitaleDoerfer/Web/platform-admin-ui/-/tags/advanced-error-handling
				// for a more advanced version.
				errorResponse = {
					type: ClientExceptionEntityTypeEnum.UnspecifiedError,
					message: `HTTP ${context.response.status} ${context.response.statusText}`
				};
			}
			throw new ClientExceptionError(errorResponse);
		}
	}
}

class DigitaleDoerferAPIFactory {
	private static middleWare: Middleware;

	public static createMiddleware(): void {
		DigitaleDoerferAPIFactory.middleWare = new DigitaleDoerferMiddleware();
	}

	public static createDigitaleDoerferAPI<T extends BaseAPI>(api: T): T {
		if (!DigitaleDoerferAPIFactory.middleWare) {
			throw new Error('Middleware not initialised!');
		}
		return api.withMiddleware(DigitaleDoerferAPIFactory.middleWare);
	}
}

export default DigitaleDoerferAPIFactory;
